import React, { useState, useEffect, useMemo, useCallback, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import './App.css';
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import { AdminProvider, AdminContext } from './AdminContext';
import { MusicPlayerProvider, useMusicPlayer } from './MusicPlayerContext';
import { showUiHint } from './utils/uiHint';
import { UserProvider, useUser } from './UserContext';
import SEO from './components/SEO';
import { LEGAL_INFO } from './utils/legalInfo';
import { theme, buttonClass } from './ui/theme';
import { trackPageView, trackHeartbeat } from './utils/activityTracker';

// 核心组件：立即加载（首屏必需）
import NoticeEditor from './components/NoticeEditor';
import AdminLoginModal from './components/AdminLoginModal';
import MusicList from './components/MusicList';
import MusicPlayer from './components/MusicPlayer';
import { TuacDefaultsProvider } from './components/musicPlayer/TuacDefaultsContext';
import MusicFavorites from './components/MusicFavorites';
import MusicHistory from './components/MusicHistory';
import PlaylistManager from './components/PlaylistManager';
import MusicDetail from './components/MusicDetail';
import AdvancedSearch from './components/AdvancedSearch';
import MusicRankings from './components/MusicRankings';
import InstallPrompt from './components/InstallPrompt';
import HomeLanding from './components/HomeLanding';
import HomeGlassCard from './components/home/HomeGlassCard';
import FooterParticleWaves from './components/FooterParticleWaves';

// 懒加载：管理页面（不常用，按需加载）
const MusicManagement = lazy(() => import('./components/MusicManagement'));
const TranslationManagement = lazy(() => import('./components/TranslationManagement'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const TwoFactorSettings = lazy(() => import('./components/TwoFactorSettings'));
const AdminAnalytics = lazy(() => import('./components/AdminAnalytics'));
const AdminDatabase = lazy(() => import('./components/AdminDatabase'));

// 懒加载：用户认证页面（非首屏）
const LoginPage = lazy(() => import('./components/auth/LoginPage'));
const RegisterPage = lazy(() => import('./components/auth/RegisterPage'));
const UserProfile = lazy(() => import('./components/auth/UserProfile'));
const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/auth/ResetPassword'));

// 懒加载：静态内容页面（可静态化）
const AboutSite = lazy(() => import('./components/AboutSite'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const RefundPolicy = lazy(() => import('./components/RefundPolicy'));
const OfflineManager = lazy(() => import('./components/OfflineManager'));
const TuacProExperience = lazy(() => import('./components/TuacProExperience'));

// 语言前缀归一化：
// - URL 里允许出现 en-US/en-GB/zh/zh-HK 等，但应用内部只使用 ja/en/cn/tw
const SUPPORTED_LANGS = ['ja', 'en', 'cn', 'tw'];
function normalizeLangLike(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  if (SUPPORTED_LANGS.includes(s)) return s;

  const lower = s.toLowerCase().replace('_', '-');

  // 英文：en-US/en-GB/... -> en
  if (lower === 'en' || lower.startsWith('en-')) return 'en';
  // 日文：ja-JP/... -> ja
  if (lower === 'ja' || lower.startsWith('ja-')) return 'ja';
  // 中文：尽量区分简繁
  // 兼容旧前缀：中文地区码/zh-* -> cn/tw
  if (lower === 'zh' || lower.startsWith('zh-') || lower === 'zh\u002dcn' || lower === 'zh\u002dtw') {
    if (lower.includes('tw') || lower.includes('hk') || lower.includes('hant')) return 'tw';
    return 'cn';
  }

  return null;
}

// 加载中占位组件
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="text-white/60">加载中...</div>
  </div>
);

// 音乐页面标签页组件
function MusicPageTabs({ languageCategory }) {
  const { t } = useTranslation();
  const getTabFromUrl = () => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const tab = params.get('tab');
      const allowed = new Set(['library', 'rankings', 'playlists', 'favorites', 'history', 'search']);
      if (tab && allowed.has(tab)) return tab;
    } catch (_) {}
    return 'library';
  };

  const [activeTab, setActiveTab] = React.useState(() => getTabFromUrl());
  const [currentCategory, setCurrentCategory] = React.useState(languageCategory);

  // 允许外部（例如顶部导航）直接切换音乐页的 tab（不污染 hash，避免与分类 hash 冲突）
  React.useEffect(() => {
    const onTab = (e) => {
      try {
        const tab = e?.detail?.tab;
        if (!tab) return;
        setActiveTab(String(tab));
        // 切到音乐库时，清空分类（显示全部）
        if (String(tab) === 'library') setCurrentCategory(null);
      } catch (_) {}
    };
    window.addEventListener('app:music-tab', onTab);
    return () => window.removeEventListener('app:music-tab', onTab);
  }, []);

  // 支持通过 URL 参数直接打开指定 tab（例如 /?tab=rankings）
  React.useEffect(() => {
    const apply = () => {
      const tab = getTabFromUrl();
      setActiveTab(tab);
      if (tab === 'library') setCurrentCategory(null);
    };
    apply();
    window.addEventListener('popstate', apply);
    window.addEventListener('app:navigate', apply);
    window.addEventListener('app:navigate-react', apply);
    return () => {
      window.removeEventListener('popstate', apply);
      window.removeEventListener('app:navigate', apply);
      window.removeEventListener('app:navigate-react', apply);
    };
  }, []);

  // 监听 languageCategory 变化
  React.useEffect(() => {
    setCurrentCategory(languageCategory);
    // 如果分类变化，自动切换到音乐库标签页
    if (languageCategory !== null) {
      setActiveTab('library');
    }
  }, [languageCategory]);

  const tabs = [
    { id: 'library', label: t('navigation.musicLibrary') },
    { id: 'favorites', label: t('navigation.myFavorites') },
    { id: 'history', label: t('navigation.playHistory') },
    { id: 'playlists', label: t('navigation.playlist') },
    { id: 'search', label: t('navigation.advancedSearch') },
    { id: 'rankings', label: t('navigation.rankings') }
  ];

  // 处理标签页切换
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    // 如果点击音乐库标签，清空分类（显示全部）
    if (tabId === 'library') {
      setCurrentCategory(null);
    }
  };

  return (
    <div className="space-y-0">
      {/* 标签页导航 */}
      <div
        className={[theme.surfaces.glassCard, 'rounded-t-lg rounded-b-none shadow-lg px-2 pt-1.5 pb-0 border-b-0'].join(' ')}
        style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
      >
        <div className="flex flex-nowrap gap-0.5 overflow-x-auto sm:gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={
                activeTab === tab.id
                  ? 'flex-none shrink-0 whitespace-nowrap px-1.5 py-1 text-[12px] leading-none font-medium text-white rounded-t-md border border-emerald-400/35 border-b-0 bg-gradient-to-r from-emerald-500/35 via-green-500/30 to-emerald-500/35 sm:min-w-[120px] sm:px-6 sm:py-1.5 sm:text-sm'
                  : 'flex-none shrink-0 whitespace-nowrap px-1.5 py-1 text-[12px] leading-none font-medium text-white/60 rounded-md border border-transparent hover:text-white hover:bg-white/[0.05] sm:min-w-[120px] sm:px-6 sm:py-1.5 sm:text-sm'
              }
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 标签页内容 */}
      <div
        className={[theme.surfaces.glassCard, 'rounded-b-lg rounded-t-none shadow-lg p-3 border-t-0'].join(' ')}
        style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
      >
        {activeTab === 'library' && (
          <div>
            <MusicListWithPlayer languageCategory={currentCategory} />
          </div>
        )}
        
        {activeTab === 'favorites' && (
          <MusicFavorites />
        )}
        
        {activeTab === 'history' && (
          <MusicHistory />
        )}
        
        {activeTab === 'playlists' && (
          <PlaylistManager />
        )}
        
        {activeTab === 'search' && (
          <AdvancedSearch />
        )}
        
        {activeTab === 'rankings' && (
          <MusicRankings />
        )}
      </div>
    </div>
  );
}

// 带播放器功能的音乐列表组件
function MusicListWithPlayer({ languageCategory }) {
  const { setCurrentMusic, setPlaylist, setPlaying } = useMusicPlayer();
  const [allMusics, setAllMusics] = React.useState([]);

  const handleMusicSelect = (music) => {
    // 设置当前播放音乐
    setCurrentMusic(music);
    setPlaying(true);
    
    // 将整个列表设置为播放列表
    if (allMusics.length > 0) {
      setPlaylist(allMusics);
    } else {
      // 如果列表还没加载，先添加当前音乐
      setPlaylist([music]);
    }
  };

  const handleMusicsLoaded = (musics) => {
    setAllMusics(musics);
    // 如果还没有播放列表，设置整个列表为播放列表
    // 这里可以通过 context 检查，暂时先设置
  };

  return (
    <MusicList
      languageCategory={languageCategory}
      onMusicSelect={handleMusicSelect}
      onMusicsLoaded={handleMusicsLoaded}
    />
  );
}

function App() {
  const { i18n, t } = useTranslation();
  const [swUpdateReady, setSwUpdateReady] = useState(false);
  const [showNoticeEditor, setShowNoticeEditor] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const VERSION_KEY = 'tenryu:build-version';

  const clearNonMusicCache = useCallback(() => {
    return new Promise((resolve) => {
      if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
        resolve(false);
        return;
      }
      const channel = new MessageChannel();
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resolve(true);
      };
      channel.port1.onmessage = finish;
      try {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_NON_MUSIC_CACHE' }, [channel.port2]);
      } catch (_) {
        finish();
      }
      setTimeout(finish, 3000);
    });
  }, []);

  const [musicCategory, setMusicCategory] = useState(() => {
    // 从 URL hash 获取分类
    const hash = window.location.hash.slice(1);
    const categoryMap = {
      'zh': 'cn',
      'ja': 'ja',
      'en': 'en',
      'world': 'world',
      'pure': 'instrumental'
    };
    return categoryMap[hash] || null;
  });
  const [staticHtmlUrl, setStaticHtmlUrl] = useState(() => {
    return window.location.pathname + window.location.search + window.location.hash;
  });
  const [view, setView] = useState(() => {
    const path = window.location.pathname;
    // 先检查是否是音乐详情页（路径包含 /music/数字）
    if (path.match(/\/music\/\d+(?:\.html)?(\/|$)/)) {
      return 'music-detail';
    }
    if (/\.html$/.test(path) && path !== '/index.html') {
      return 'static-html';
    } else if (path.match(/^\/(music)(\/|$)/)) {
      // 显式音乐页（无语言前缀）
      return 'music';
    } else if (path.includes('/about')) {
      return 'about';
    } else if (path.includes('/terms')) {
      return 'terms';
    } else if (path.includes('/privacy')) {
      return 'privacy';
    } else if (path.includes('/refund')) {
      return 'refund';
    } else if (path.includes('/checkout')) {
      return 'profile';
    } else if (path.includes('/offline')) {
      return 'offline';
    } else if (path.includes('/translation-management')) {
      return 'translation-management';
    } else if (path.includes('/music-management')) {
      return 'music-management';
    } else if (path.includes('/user-management')) {
      return 'user-management';
    } else if (path.includes('/database-management')) {
      return 'admin-database';
    } else if (path.includes('/2fa-settings')) {
      return '2fa-settings';
    } else if (path.includes('/forgot-password')) {
      return 'forgot-password';
    } else if (path.includes('/reset-password')) {
      return 'reset-password';
    } else if (path.includes('/login')) {
      return 'login';
    } else if (path.includes('/register')) {
      return 'register';
    } else if (path.includes('/profile')) {
      return 'profile';
    } else if (path.includes('/tuac-pro')) {
      return 'tuac-pro';
    }
    // 站点首页：/ 或 /<lang>（不含其他路径）显示落地页
    try {
      const segs = path.split('/').filter(Boolean);
      const first = segs[0] || null;
      const isLangOnly = first && normalizeLangLike(first) && segs.length === 1;
      const isRoot = segs.length === 0;
      if (isRoot || isLangOnly) return 'home';
      // /<lang>/music 显式音乐页
      if (first && normalizeLangLike(first) && segs[1] === 'music') return 'music';
    } catch (_) {}
    // 其他默认落到音乐页
    return 'music';
  });

  // Service Worker 更新提示（由 registerServiceWorker 派发事件）
  useEffect(() => {
    const onUpdate = () => setSwUpdateReady(true);
    window.addEventListener('tenryu:sw-update-available', onUpdate);
    return () => window.removeEventListener('tenryu:sw-update-available', onUpdate);
  }, []);

  // 服务端版本检测：发现新版本时清理缓存并刷新（保留离线音乐）
  useEffect(() => {
    const CHECK_INTERVAL = 3 * 60 * 1000; // 3 分钟，发现新版本后自动清缓存并刷新

    const resolveVersionUrl = () => {
      let url = '/api/version';
      try {
        if (window.location.hostname === 'localhost' && String(window.location.port || '') === '3000') {
          const host = window.location.hostname || 'localhost';
          url = `${window.location.protocol}//${host}:2000/api/version`;
        }
      } catch (_) {}
      const ts = Date.now();
      return `${url}${url.includes('?') ? '&' : '?'}__ts=${ts}`;
    };

    const checkVersion = async () => {
      try {
        if (document.visibilityState !== 'visible') return;
        const res = await fetch(resolveVersionUrl(), { credentials: 'include', cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const nextVersion = String(data && data.version ? data.version : '');
        if (!nextVersion) return;
        const prevVersion = (() => {
          try { return localStorage.getItem(VERSION_KEY); } catch (_) { return null; }
        })();
        if (prevVersion && prevVersion !== nextVersion) {
          try { localStorage.setItem(VERSION_KEY, nextVersion); } catch (_) {}
          await clearNonMusicCache();
          window.location.reload();
          return;
        }
        if (!prevVersion) {
          try { localStorage.setItem(VERSION_KEY, nextVersion); } catch (_) {}
        }
      } catch (_) {}
    };

    checkVersion();
    const timer = setInterval(checkVersion, CHECK_INTERVAL);
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [VERSION_KEY, clearNonMusicCache]);

  // 管理员入口：手动触发全站刷新版本
  useEffect(() => {
    const handleForceRefresh = async () => {
      const confirmText = t('navigation.forceRefreshConfirm', '确定刷新客户端缓存并更新到新版本？');
      if (!window.confirm(confirmText)) return;
      try {
        const res = await fetch('/api/admin/refresh-version', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data && data.success !== false) {
          try {
            if (data.version) localStorage.setItem(VERSION_KEY, data.version);
          } catch (_) {}
          await clearNonMusicCache();
          showUiHint(t('navigation.forceRefreshDone', '已触发刷新，客户端将自动更新。'));
          window.location.reload();
          return;
        }
      } catch (_) {}
      showUiHint(t('navigation.forceRefreshFailed', '刷新失败，请稍后重试。'));
    };
    window.addEventListener('app:force-refresh', handleForceRefresh);
    return () => window.removeEventListener('app:force-refresh', handleForceRefresh);
  }, [VERSION_KEY, clearNonMusicCache, t]);

  // 管理员入口：仅清理缓存不刷新
  useEffect(() => {
    const handleClearCache = async () => {
      const confirmText = t('navigation.clearCacheConfirm', '确定清理客户端缓存（不刷新页面）？');
      if (!window.confirm(confirmText)) return;
      await clearNonMusicCache();
      showUiHint(t('navigation.clearCacheDone', '已清理缓存，下次刷新将加载新版本。'));
    };
    window.addEventListener('app:clear-cache', handleClearCache);
    return () => window.removeEventListener('app:clear-cache', handleClearCache);
  }, [clearNonMusicCache, t]);

  function resolveStaticFetchUrl(url) {
    try {
      if (!url) return url;
      if (window.location.hostname === 'localhost' && String(window.location.port || '') === '3000' && url.indexOf('/') === 0) {
        const host = window.location.hostname || 'localhost';
        url = `${window.location.protocol}//${host}:2000${url}`;
      }
      const ts = Date.now();
      return `${url}${url.includes('?') ? '&' : '?'}__ts=${ts}`;
    } catch (_) {}
    return url;
  }

  const StaticHtmlView = ({ url }) => {
    const [html, setHtml] = useState('');
    const [loadingStatic, setLoadingStatic] = useState(true);
    const [staticError, setStaticError] = useState('');

    useEffect(() => {
      let cancelled = false;
      const fetchUrl = resolveStaticFetchUrl(url);
      setLoadingStatic(true);
      setStaticError('');
      fetch(fetchUrl, { credentials: 'include', cache: 'no-store' })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.text();
        })
        .then((text) => {
          if (cancelled) return;
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const newRoot = doc.getElementById('root');
          if (!newRoot) throw new Error('missing-root');
          const titleEl = doc.querySelector('title');
          if (titleEl) document.title = titleEl.textContent || document.title;
          const htmlLang = doc.documentElement && doc.documentElement.getAttribute('lang');
          if (htmlLang) document.documentElement.setAttribute('lang', htmlLang);
          setHtml(newRoot.innerHTML || '');

          // 执行静态页的内联配置（如 TENRYU_PLAYLIST）
          try {
            try { delete window.TENRYU_PLAYLIST; } catch (_) {}
            const scripts = doc.querySelectorAll('script');
            scripts.forEach((sc) => {
              if (sc && !sc.src && sc.textContent && sc.textContent.includes('TENRYU_PLAYLIST')) {
                try {
                  const inline = document.createElement('script');
                  inline.text = sc.textContent;
                  document.head.appendChild(inline);
                  document.head.removeChild(inline);
                } catch (_) {}
              }
            });
          } catch (_) {}

          // 确保 playlist-runtime 执行（如果存在）
          try {
            const runPlaylist = () => {
              try {
                if (typeof window.__TENRYU_PLAYLIST_BUILD === 'function') {
                  window.__TENRYU_PLAYLIST_BUILD();
                }
              } catch (_) {}
            };
            if (typeof window.__TENRYU_PLAYLIST_BUILD === 'function') {
              runPlaylist();
            } else {
              const ps = document.createElement('script');
              ps.src = '/playlist-runtime.js';
              ps.onload = runPlaylist;
              ps.onerror = () => {};
              document.head.appendChild(ps);
            }
          } catch (_) {}
        })
        .catch(() => {
          if (cancelled) return;
          setStaticError('load-failed');
        })
        .finally(() => {
          if (!cancelled) setLoadingStatic(false);
        });
      return () => { cancelled = true; };
    }, [url]);

    if (staticError) {
      // 回退到整页跳转
      try { window.location.href = url; } catch (_) {}
      return null;
    }

    if (loadingStatic) {
      return (
        <div className="flex items-center justify-center min-h-[240px] text-white/60">
          加载中...
        </div>
      );
    }

    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  // 检测路径并处理 SPA 路由
  useEffect(() => {
    const handleLocationChange = () => {
    let path = window.location.pathname;
    const segments = path.split('/').filter(Boolean);
    // 先把 URL 中“别名语言前缀”规整为站内规范前缀，避免后续语言同步逻辑来回改 URL 造成请求被 abort。
    // 例如：/en-US/music/92 -> /en/music/92
    try {
      if (segments.length > 0) {
        const raw = segments[0];
        const canonical = normalizeLangLike(raw);
        if (canonical && raw !== canonical) {
          const newSegments = segments.slice();
          newSegments[0] = canonical;
          const newPath = '/' + newSegments.join('/');
          if (newPath !== path) {
            window.history.replaceState({ url: newPath }, '', newPath);
            path = newPath;
          }
        }
      }
    } catch (_) {}
    
      // 处理语言
      // 约定：URL 语言前缀优先。访问 /cn 或 /tw 必须显示对应语言，并同步写入 localStorage，避免导航/内容语言不一致。
      const segs2 = path.split('/').filter(Boolean);
      const urlLang = (segs2.length > 0) ? normalizeLangLike(segs2[0]) : null;
      if (urlLang) {
        const lang = urlLang;
        try { localStorage.setItem('i18nextLng', lang); } catch (_) {}
        if (i18n.language !== lang) {
          i18n.changeLanguage(lang);
        }
      } else if (path === '/' || path === '') {
        // 如果访问根路径 / 且没有语言前缀
        const savedLang = localStorage.getItem('i18nextLng');
        if (!savedLang) {
          // 检查浏览器语言，但只有中文/日文才使用，其他语言使用英文
          const browserLang = navigator.language || navigator.userLanguage || '';
          let targetLang = 'en'; // 默认英文
          
          if (browserLang.startsWith('ja')) {
            targetLang = 'ja';
          } else if (browserLang.startsWith('zh')) {
            const lower = String(browserLang).toLowerCase();
            targetLang = (lower.includes('tw') || lower.includes('hk') || lower.includes('hant')) ? 'tw' : 'cn';
          }
          // 其他语言（包括英文）都使用英文
          
          if (i18n.language !== targetLang) {
            i18n.changeLanguage(targetLang);
          }
        }
      }

      const isMusicDetail = path.match(/\/music\/\d+(?:\.html)?(\/|$)/);
      if (!isMusicDetail && /\.html$/.test(path) && path !== '/index.html') {
        const fullUrl = window.location.pathname + window.location.search + window.location.hash;
        setStaticHtmlUrl(fullUrl);
        setView('static-html');
        // 采集：page_view（失败静默）
        try {
          trackPageView({
            path: fullUrl,
            title: document.title || '',
            referrer: document.referrer || ''
          });
        } catch (_) {}
        return;
      }

      // 处理视图切换
      // 先检查是否是音乐详情页（路径包含 /music/数字）
      if (isMusicDetail) {
        // 音乐详情页：/music/2 或 /cn/music/2
        setView('music-detail');
      } else if (path.match(/^\/(music)(\/|$)/)) {
        // 显式音乐页（无语言前缀）
        setView('music');
      } else if (path.includes('/offline')) {
        setView('offline');
      } else if (path.includes('/admin-analytics')) {
        setView('admin-analytics');
      } else if (path.includes('/translation-management')) {
        setView('translation-management');
      } else if (path.includes('/music-management')) {
        // 音乐管理页
        setView('music-management');
      } else if (path.includes('/user-management')) {
        // 用户管理页
        setView('user-management');
      } else if (path.includes('/database-management')) {
        setView('admin-database');
      } else if (path.includes('/forgot-password')) {
        setView('forgot-password');
      } else if (path.includes('/reset-password')) {
        setView('reset-password');
      } else if (path.includes('/login')) {
        setView('login');
      } else if (path.includes('/register')) {
        setView('register');
      } else if (path.includes('/profile')) {
        setView('profile');
      } else if (path.includes('/tuac-pro')) {
        setView('tuac-pro');
      } else if (path.includes('/about')) {
        setView('about');
      } else if (path.includes('/terms')) {
        setView('terms');
      } else if (path.includes('/privacy')) {
        setView('privacy');
      } else if (path.includes('/refund')) {
        setView('refund');
      } else if (path.includes('/checkout')) {
        try {
          const segs2 = path.split('/').filter(Boolean);
          const urlLang = (segs2.length > 0) ? normalizeLangLike(segs2[0]) : null;
          const fallback = urlLang ? `/${urlLang}/profile` : '/profile';
          window.history.replaceState({ url: fallback }, '', fallback);
        } catch (_) {}
        setView('profile');
      } else {
        // 首页：/ 或 /<lang>（不含其他路径）显示落地页，其它默认音乐页
        try {
          const segs = path.split('/').filter(Boolean);
          const first = segs[0] || null;
          const isLangOnly = first && normalizeLangLike(first) && segs.length === 1;
          const isRoot = segs.length === 0;
          if (isRoot || isLangOnly) {
            setView('home');
          } else if (first && normalizeLangLike(first) && segs[1] === 'music') {
            setView('music');
          } else {
            setView('music');
          }
        } catch (_) {
          setView('music');
        }
      }

      // 同步 hash 分类（pushState 不会触发 hashchange）
      try {
        setMusicCategory(resolveMusicCategory(window.location.hash));
      } catch (_) {}

      // 采集：page_view（失败静默）
      try {
        trackPageView({
          path: window.location.pathname + window.location.search + window.location.hash,
          title: document.title || '',
          referrer: document.referrer || ''
        });
      } catch (_) {}
    };

    const resolveMusicCategory = (raw) => {
      try {
        const key = String(raw || '').replace(/^#/, '');
        if (!key) return null;
        const categoryMap = {
          'zh': 'cn',
          'ja': 'ja',
          'en': 'en',
          'world': 'world',
          'pure': 'instrumental'
        };
        return categoryMap[key] || null;
      } catch (_) {
        return null;
      }
    };

    // 处理音乐分类变化
    const handleHashChange = () => {
      setMusicCategory(resolveMusicCategory(window.location.hash));
    };

    // 监听音乐分类事件
    const handleMusicCategoryEvent = (e) => {
      // 允许 category 为空：用于“清空分类”（显示全部歌曲）
      const category = e?.detail?.category;
      if (!category) {
        setMusicCategory(null);
        return;
      }
      setMusicCategory(resolveMusicCategory(category));
    };

    // 初始检测
    handleLocationChange();
    handleHashChange();

    // 监听前进后退
    window.addEventListener('popstate', handleLocationChange);
    // 监听自定义跳转事件
    window.addEventListener('app:navigate', handleLocationChange);
    // 监听 hash 变化
    window.addEventListener('hashchange', handleHashChange);
    // 监听音乐分类事件
    window.addEventListener('app:music-category', handleMusicCategoryEvent);

    // 监听 header.js 发出的“打开会员登录”
    const handleOpenLogin = () => {
       // 离线状态下不允许登录/注册
       if (!navigator.onLine) {
         showUiHint(t('hints.networkOffline'));
         return;
       }
       
       const path = window.location.pathname;
       const segments = path.split('/').filter(Boolean);
       let prefix = '';
       const lang = (segments.length > 0) ? normalizeLangLike(segments[0]) : null;
       if (lang) {
         prefix = '/' + lang;
       }
       window.history.pushState({}, '', prefix + '/login');
       window.dispatchEvent(new Event('app:navigate'));
    };
    window.addEventListener('app:open-login', handleOpenLogin);
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('app:navigate', handleLocationChange);
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('app:music-category', handleMusicCategoryEvent);
      window.removeEventListener('app:open-login', handleOpenLogin);
    };
  }, [i18n, t]);

  // 心跳：用于估算停留时间（每 30 秒一次；页面隐藏时暂停）
  useEffect(() => {
    const timer = setInterval(() => {
      try {
        if (document.visibilityState !== 'visible') return;
        trackHeartbeat({
          path: window.location.pathname + window.location.search + window.location.hash,
          durationSeconds: 30
        });
      } catch (_) {}
    }, 30000);
    return () => {
      try { clearInterval(timer); } catch (_) {}
    };
  }, []);

  // header.js 跨页面触发“音乐排行榜/工作室”时会写入 sessionStorage：这里在进入音乐页后应用一次
  useEffect(() => {
    if (view !== 'music') return;
    try {
      const tab = sessionStorage.getItem('pendingMusicTab');
      const at = Number(sessionStorage.getItem('pendingMusicTabAt') || 0);
      // 只接受近 30 秒内的请求，避免旧值污染
      if (tab && at && (Date.now() - at) < 30000) {
        try { window.dispatchEvent(new CustomEvent('app:music-tab', { detail: { tab } })); } catch (_) {}
      }
      sessionStorage.removeItem('pendingMusicTab');
      sessionStorage.removeItem('pendingMusicTabAt');
    } catch (_) {}
  }, [view]);

  // 通知 header.js 登录状态变化（用于更新"会员登录"按钮为"注销"）
  const AdminStatusBridge = () => {
    const { isAdmin, logout } = React.useContext(AdminContext);
    const { user, logout: userLogout } = useUser();
    
    React.useEffect(() => {
      try {
        window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
          // 顶部导航里“会员登录 ↔ 注销”取决于 isLoggedIn（不应被 isAdmin 单独覆盖）
          detail: { isAdmin: !!isAdmin, isLoggedIn: !!isAdmin || !!user } 
        }));
      } catch (_) {}
    }, [isAdmin, user]);

    React.useEffect(() => {
      const handleLogout = () => {
        // 顶部导航的“注销”按钮统一触发 app:logout
        // - 若是管理员：注销管理员登录态
        // - 否则：注销普通用户登录态
        try {
          if (isAdmin) {
            logout();
          } else if (user) {
            userLogout();
          }
        } catch (_) {}
      };
      window.addEventListener('app:logout', handleLogout);
      return () => {
        window.removeEventListener('app:logout', handleLogout);
      };
    }, [logout, userLogout, isAdmin, user]);

    return null;
  };

  // SPA 跳转函数
  const navigateTo = (path) => {
    // 如果是静态 HTML 文件（如 terms.html, privacy.html 等），使用完整页面跳转
    // 这样可以确保加载最新的静态文件，避免缓存问题
    if (path && /\.html$/.test(path) && path !== '/index.html') {
      window.location.href = path;
      return;
    }
    // 其他路径使用 SPA 路由
    window.history.pushState({}, '', path);
    window.dispatchEvent(new Event('app:navigate'));
  };

  // 公告编辑弹窗打开时，禁止背景滚动
  useEffect(() => {
    if (!showNoticeEditor) return;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [showNoticeEditor]);

  // 监听头部发出的语言切换事件，避免整页刷新
  useEffect(() => {
    const onLang = (e) => {
      try {
        const raw = (e && e.detail && e.detail.lang) || i18n.language;
        const lang = normalizeLangLike(raw) || raw;
        if (lang && i18n.language !== lang) {
          // 语言码统一归一化，避免 i18n 进入 en-US 这类“非资源语言”引发循环
          i18n.changeLanguage(normalizeLangLike(lang) || lang);
          // 更新 URL 路径以匹配新语言
          updateUrlForLanguage(lang);
        }
      } catch(_){}
    };
    
    // 更新 URL 路径以匹配当前语言
    const updateUrlForLanguage = (newLang) => {
      const canonicalLang = normalizeLangLike(newLang);
      if (!canonicalLang) return;
      const path = window.location.pathname;
      const segments = path.split('/').filter(Boolean);
      
      // 如果路径中有语言代码，替换它
      if (segments.length > 0 && normalizeLangLike(segments[0])) {
        segments[0] = canonicalLang;
      } else {
        // 如果路径中没有语言代码，在开头添加
        segments.unshift(canonicalLang);
      }
      
      const newPath = '/' + segments.join('/');
      if (newPath !== path) {
        window.history.pushState({ url: newPath }, '', newPath);
        // 触发 header 重新渲染
        try {
          window.dispatchEvent(new Event('app:navigate'));
          // 触发语言变化事件，让 header 重新渲染
          window.dispatchEvent(new CustomEvent('i18n:language-changed', { detail: { lang: newLang } }));
          // 如果 header 有 render 函数，也可以直接调用
          if (typeof window.__TENRYU_HEADER_RENDER === 'function') {
            setTimeout(() => {
              window.__TENRYU_HEADER_RENDER();
            }, 50);
          }
        } catch(_) {}
      }
    };
    
    // 监听 i18n 语言变化事件，自动更新 URL
    const onI18nLangChange = (lng) => {
      const nl = normalizeLangLike(lng);
      if (nl) {
        updateUrlForLanguage(nl);
      }
    };
    
    i18n.on('languageChanged', onI18nLangChange);
    
    window.addEventListener('app:language-change', onLang);
    return () => {
      window.removeEventListener('app:language-change', onLang);
      i18n.off('languageChanged', onI18nLangChange);
    };
  }, [i18n]);

  const RequireAdmin = ({ children }) => {
    const { isAdmin } = React.useContext(AdminContext);
    if (isAdmin) return children;
    return (
      <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-6'].join(' ')}>
        <div className="text-white font-semibold mb-2">需要管理员登录</div>
        <div className="text-sm text-white/70 mb-3">请通过顶部导航的“会员登录”入口登录后再使用该功能。</div>
        <button className={buttonClass({ variant: 'outline', size: 'sm' })} onClick={() => setLoginOpen(true)}>会员登录</button>
      </div>
    );
  };

  // 顶部导航动作桥接：发布公告 / 文件管理
  const AdminNavBridge = () => {
    const { isAdmin } = React.useContext(AdminContext);
    const [pending, setPending] = React.useState(null); // 'notice' | 'file' | null

    const openFileManager = React.useCallback(() => {
      try {
        if (typeof window !== 'undefined' && typeof window.openFileManagerModal === 'function') {
          window.openFileManagerModal();
          return;
        }
        // 动态加载公共脚本后再打开
        const s = document.createElement('script');
        s.src = '/file-manager.js';
        s.defer = true;
        s.onload = () => {
          try {
            if (typeof window.openFileManagerModal === 'function') window.openFileManagerModal();
            else showUiHint(t('hints.fileManagerLoadFailed'));
          } catch (_) {
            showUiHint(t('hints.fileManagerLoadFailed'));
          }
        };
        s.onerror = () => showUiHint(t('hints.fileManagerScriptLoadFailed'));
        document.head.appendChild(s);
      } catch (_) {
        showUiHint(t('hints.fileManagerOpenFailed'));
      }
    }, []);

    React.useEffect(() => {
      const onNotice = () => {
        if (!isAdmin) {
          setPending('notice');
          setLoginOpen(true);
          return;
        }
        setShowNoticeEditor(true);
      };
      const onFile = () => {
        if (!isAdmin) {
          setPending('file');
          setLoginOpen(true);
          return;
        }
        openFileManager();
      };
      window.addEventListener('app:open-notice-editor', onNotice);
      window.addEventListener('app:open-file-manager', onFile);
      return () => {
        window.removeEventListener('app:open-notice-editor', onNotice);
        window.removeEventListener('app:open-file-manager', onFile);
      };
    }, [isAdmin, openFileManager]);

    // 登录成功后自动执行一次“刚才想做的动作”
    React.useEffect(() => {
      if (!isAdmin || !pending) return;
      if (pending === 'notice') setShowNoticeEditor(true);
      if (pending === 'file') openFileManager();
      setPending(null);
    }, [isAdmin, pending, openFileManager]);

    return null;
  };

  // 获取当前语言
  const currentLang = i18n.language || 'ja';
  
  // 构建法律文档链接（根据当前语言）
  const legalLinks = useMemo(() => {
    const lang = normalizeLangLike(currentLang) || 'ja';
    const langPrefix = lang === 'ja' ? '' : `/${lang}`;
    const build = (path) => langPrefix ? `${langPrefix}${path}` : path;
    return {
      terms: build('/terms.html'),
      privacy: build('/privacy.html'),
      refund: build('/refund.html'),
      tokusho: build('/tokusho.html')
    };
  }, [currentLang]);
  
  // 生成 hreflang 链接
  const siteUrl = typeof window !== 'undefined' 
    ? `${window.location.protocol}//${window.location.host}`
    : 'https://tianliu.ai';
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const hreflangLinks = [
    { lang: 'ja', url: `${siteUrl}/ja${currentPath.replace(/^\/(ja|en|cn|tw)/, '') || ''}` },
    { lang: 'cn', url: `${siteUrl}/cn${currentPath.replace(/^\/(ja|en|cn|tw)/, '') || ''}` },
    { lang: 'tw', url: `${siteUrl}/tw${currentPath.replace(/^\/(ja|en|cn|tw)/, '') || ''}` },
    { lang: 'en', url: `${siteUrl}/en${currentPath.replace(/^\/(ja|en|cn|tw)/, '') || ''}` },
    { lang: 'x-default', url: `${siteUrl}/ja${currentPath.replace(/^\/(ja|en|cn|tw)/, '') || ''}` }
  ];
  
  // 根据当前视图生成 SEO 信息
  const getSEOProps = () => {
    const baseTitle = '天流音楽 - わたしは天流れ、わたしは命';
    const baseDescription = '天流音楽 - 多语言音乐平台，提供中文、日文、英文及世界音乐。天流音楽，わたしは天流れ、わたしは命。';
    
    switch (view) {
      case 'music-detail':
        return {
          title: baseTitle,
          description: baseDescription,
          type: 'music.song'
        };
      case 'about':
        return {
          title: `${t('navigation.aboutSite', '本站介绍')} - ${baseTitle}`,
          description: baseDescription,
          type: 'website'
        };
      case 'login':
        return {
          title: `${t('navigation.login')} - ${baseTitle}`,
          description: baseDescription,
          type: 'website'
        };
      case 'register':
        return {
          title: `${t('navigation.register')} - ${baseTitle}`,
          description: baseDescription,
          type: 'website'
        };
      case 'forgot-password':
        return {
          title: `${t('auth.forgotPasswordTitle')} - ${baseTitle}`,
          description: baseDescription,
          type: 'website'
        };
      case 'reset-password':
        return {
          title: `${t('auth.resetPasswordTitle')} - ${baseTitle}`,
          description: baseDescription,
          type: 'website'
        };
      default:
        return {
          title: baseTitle,
          description: baseDescription,
          type: 'website'
        };
    }
  };
  
  return (
    <HelmetProvider>
    <AdminProvider>
    <UserProvider>
    <MusicPlayerProvider>
    <div
      className="min-h-screen flex flex-col"
      style={{
        // 注意：暗色背景使用 background（包含颜色层），不要用 backgroundImage
        background:
          view === 'home'
            ? 'radial-gradient(1200px 600px at 10% 10%, rgba(59,130,246,.18), transparent 45%), radial-gradient(900px 500px at 90% 20%, rgba(16,185,129,.14), transparent 55%), #0b1220'
            : 'radial-gradient(1200px 600px at 10% 10%, rgba(59,130,246,.14), transparent 45%), radial-gradient(900px 500px at 90% 20%, rgba(16,185,129,.10), transparent 55%), #0b1220',
        // 播放器为 fixed 浮动覆盖：页面不预留任何空间
        paddingBottom: 0
      }}
    >
      {swUpdateReady && (
        <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[9999]">
          <div className={[theme.surfaces.modalPanel, 'text-white/95 shadow-xl rounded-md px-4 py-2 flex items-center gap-3'].join(' ')}>
            <div className="text-sm">检测到新版本已就绪</div>
            <button
              className={buttonClass({ variant: 'outline', size: 'sm' })}
              onClick={() => window.location.reload()}
            >
              刷新更新
            </button>
            <button
              className={buttonClass({ variant: 'ghost', size: 'sm' })}
              onClick={() => setSwUpdateReady(false)}
            >
              稍后
            </button>
          </div>
        </div>
      )}
      <SEO {...getSEOProps()} hreflang={hreflangLinks} />
      {view !== 'static-html' && <Header />}
      <AdminNavBridge />
      <AdminStatusBridge />

      {/* 首页/音乐页/离线/关于/结算/音乐管理/TUAC专业体验：全宽布局，左右固定 4px；其它页面维持 container */}
      <div className={(view === 'home' || view === 'music' || view === 'offline' || view === 'about' || view === 'music-management' || view === 'tuac-pro' || view === 'static-html' ? 'px-1 pt-1 pb-2' : 'container mx-auto px-1 pt-1 pb-2') + ' flex-1'}>
        <Suspense fallback={<LoadingFallback />}>
          {view === 'home' ? (
            <HomeLanding currentLang={currentLang} onNavigate={navigateTo} />
          ) : view === 'offline' ? (
            <>
              <OfflineManager />
            </>
          ) : view === 'tuac-pro' ? (
            createPortal(
              <div
                className="fixed inset-0 z-[9998] overflow-hidden flex justify-center items-stretch"
                style={{
                  height: '100vh',
                  background:
                    'radial-gradient(1200px 600px at 10% 10%, rgba(59,130,246,.14), transparent 45%), radial-gradient(900px 500px at 90% 20%, rgba(16,185,129,.10), transparent 55%), #0b1220'
                }}
              >
                <TuacProExperience onNavigate={navigateTo} />
              </div>,
              document.body
            )
          ) : view === 'about' ? (
            <>
              <AboutSite />
            </>
          ) : view === 'terms' ? (
            <>
              <TermsOfService />
            </>
          ) : view === 'privacy' ? (
            <>
              <PrivacyPolicy />
            </>
          ) : view === 'refund' ? (
            <>
              <RefundPolicy />
            </>
          ) : view === 'login' ? (
            <LoginPage onNavigate={navigateTo} />
          ) : view === 'register' ? (
            <RegisterPage onNavigate={navigateTo} />
          ) : view === 'forgot-password' ? (
            <ForgotPassword onNavigate={navigateTo} />
          ) : view === 'reset-password' ? (
            <ResetPassword onNavigate={navigateTo} />
          ) : view === 'profile' ? (
            <UserProfile onNavigate={navigateTo} />
          ) : view === 'music-management' ? (
          <>
            {/* 音乐管理页面：对齐首页的玻璃卡与宽度 */}
            <RequireAdmin>
              <section className="px-0 lg:px-1">
                <div className="max-w-6xl mx-auto px-0 lg:px-3">
                  <HomeGlassCard className="rounded-lg shadow-lg p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h1 className="text-xl font-semibold text-white">{t('navigation.musicManagement')}</h1>
                      <button
                        className={buttonClass({ variant: 'outline', size: 'sm' })}
                        onClick={() => {
                          const path = `/${currentLang}/`;
                          navigateTo(path);
                        }}
                      >
                        {t('navigation.backToLibrary')}
                      </button>
                    </div>
                    <MusicManagement />
                  </HomeGlassCard>
                </div>
              </section>
            </RequireAdmin>
          </>
        ) : view === 'translation-management' ? (
          <>
            <RequireAdmin>
              <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-6'].join(' ')}>
                <div className="flex items-center justify-between mb-4">
                  <h1 className="text-xl font-semibold text-white">{t('navigation.translationManagementTitle')}</h1>
                  <button
                    className={buttonClass({ variant: 'outline', size: 'sm' })}
                    onClick={() => {
                      const path = `/${currentLang}/`;
                      navigateTo(path);
                    }}
                  >
                    {t('navigation.backToLibrary')}
                  </button>
                </div>
                <TranslationManagement />
              </div>
            </RequireAdmin>
          </>
        ) : view === 'user-management' ? (
          <>
            <RequireAdmin>
              <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-6'].join(' ')}>
                <div className="flex items-center justify-between mb-4">
                  <h1 className="text-xl font-semibold text-white">{t('navigation.userManagement')}</h1>
                  <button
                    className={buttonClass({ variant: 'outline', size: 'sm' })}
                    onClick={() => {
                      const path = `/${currentLang}/`;
                      navigateTo(path);
                    }}
                  >
                    {t('navigation.backToLibrary')}
                  </button>
                </div>
                <UserManagement />
              </div>
            </RequireAdmin>
          </>
        ) : view === 'admin-analytics' ? (
          <>
            <RequireAdmin>
              <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-6'].join(' ')}>
                <div className="flex items-center justify-between mb-4">
                  <h1 className="text-xl font-semibold text-white">{t('navigation.analytics', '运营统计')}</h1>
                  <button
                    className={buttonClass({ variant: 'outline', size: 'sm' })}
                    onClick={() => {
                      const path = `/${currentLang}/`;
                      navigateTo(path);
                    }}
                  >
                    {t('navigation.backToLibrary')}
                  </button>
                </div>
                <AdminAnalytics />
              </div>
            </RequireAdmin>
          </>
        ) : view === 'admin-database' ? (
          <>
            <RequireAdmin>
              <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-6'].join(' ')}>
                <div className="flex items-center justify-between mb-4">
                  <h1 className="text-xl font-semibold text-white">{t('navigation.databaseManagement')}</h1>
                  <button
                    className={buttonClass({ variant: 'outline', size: 'sm' })}
                    onClick={() => {
                      const path = `/${currentLang}/`;
                      navigateTo(path);
                    }}
                  >
                    {t('navigation.backToLibrary')}
                  </button>
                </div>
                <AdminDatabase />
              </div>
            </RequireAdmin>
          </>
        ) : view === '2fa-settings' ? (
          <>
            <RequireAdmin>
              <div className={[theme.surfaces.glassCard, 'rounded-lg shadow-lg p-6'].join(' ')}>
                <div className="flex items-center justify-between mb-4">
                  <h1 className="text-xl font-semibold text-white">两步验证设置</h1>
                  <button
                    className={buttonClass({ variant: 'outline', size: 'sm' })}
                    onClick={() => {
                      const path = `/${currentLang}/`;
                      navigateTo(path);
                    }}
                  >
                    {t('navigation.backToLibrary')}
                  </button>
                </div>
                <TwoFactorSettings />
              </div>
            </RequireAdmin>
          </>
        ) : view === 'music-detail' ? (
          <>
            <MusicDetail />
          </>
        ) : view === 'static-html' ? (
          <>
            <StaticHtmlView url={staticHtmlUrl} />
          </>
        ) : (
          <>
            {/* 音乐页面内容 */}
            {/* 标签页导航 */}
            <MusicPageTabs languageCategory={musicCategory} />
          </>
        )}
        </Suspense>
                 {showNoticeEditor && (
           <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
             <NoticeEditor onSuccess={() => { setShowNoticeEditor(false); }} onCancel={() => setShowNoticeEditor(false)} />
           </div>
         )}
       </div>
      
      {/* 页脚 */}
      {view !== 'static-html' && <footer className="border-t border-white/10 bg-[#0b1220]/92 backdrop-blur-sm text-white pt-6 pb-12 mt-auto relative overflow-hidden">
        {/* 透明叠加动效：保留 footer 底色 */}
        <FooterParticleWaves />
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-sm text-white/70">
            <a 
              className="inline-flex items-center gap-2 hover:text-white"
              href={legalLinks.terms}
              onClick={(e) => {
                e.preventDefault();
                navigateTo(legalLinks.terms);
              }}
            >
              {t('navigation.termsOfService')}
            </a>
            <a 
              className="inline-flex items-center gap-2 hover:text-white"
              href={legalLinks.privacy}
              onClick={(e) => {
                e.preventDefault();
                navigateTo(legalLinks.privacy);
              }}
            >
              {t('navigation.privacyPolicy')}
            </a>
            <a
              className="inline-flex items-center gap-2 hover:text-white"
              href={legalLinks.refund}
              onClick={(e) => {
                e.preventDefault();
                navigateTo(legalLinks.refund);
              }}
            >
              {t('navigation.refundPolicy')}
            </a>
            {/* Tokusho 为静态页：不要走 SPA navigateTo，确保可直接打开 */}
            <a
              className="inline-flex items-center gap-2 hover:text-white"
              href={legalLinks.tokusho}
            >
              {t('navigation.commerceDisclosure', 'Commercial Disclosure')}
            </a>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-sm text-white/70">
            <a
              className="inline-flex items-center gap-2 hover:text-white"
              href="https://tianliu.ai/"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ display: 'inline-block', verticalAlign: '-2px' }}>
                <path d="M12 2a10 10 0 1 0 10 10A10.01 10.01 0 0 0 12 2Zm7.93 9h-3.22a15.7 15.7 0 0 0-1.07-5 8.03 8.03 0 0 1 4.29 5ZM12 4c1.27 0 2.52 2.2 3.17 7H8.83C9.48 6.2 10.73 4 12 4ZM4.07 11a8.03 8.03 0 0 1 4.29-5 15.7 15.7 0 0 0-1.07 5Zm0 2h3.22a15.7 15.7 0 0 0 1.07 5 8.03 8.03 0 0 1-4.29-5ZM12 20c-1.27 0-2.52-2.2-3.17-7h6.34C14.52 17.8 13.27 20 12 20Zm3.64-2a15.7 15.7 0 0 0 1.07-5h3.22a8.03 8.03 0 0 1-4.29 5Z" />
              </svg>
              tianliu.ai
            </a>
            <a className="inline-flex items-center gap-2 hover:text-white" href={`mailto:${LEGAL_INFO.supportEmail}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ display: 'inline-block', verticalAlign: '-2px' }}>
                <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4-8 5L4 8V6l8 5 8-5Z" />
              </svg>
              {LEGAL_INFO.supportEmail}
            </a>
            <a className="inline-flex items-center gap-2 hover:text-white" href={`tel:${LEGAL_INFO.supportPhone}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ display: 'inline-block', verticalAlign: '-2px' }}>
                <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24 11.36 11.36 0 0 0 3.56.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.36 11.36 0 0 0 .57 3.56 1 1 0 0 1-.24 1.01Z" />
              </svg>
              {LEGAL_INFO.supportPhone}
            </a>
          </div>
          <p className="mt-2 text-xs text-white/55 whitespace-nowrap">
            {t('index_footer')}
          </p>
        </div>
      </footer>}
      <AdminLoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
      
      {/* PWA安装提示 */}
      <InstallPrompt />
      
      {/* 音乐播放器 */}
      <MusicPlayerWrapper />
    </div>
    </MusicPlayerProvider>
    </UserProvider>
    </AdminProvider>
    </HelmetProvider>
  );
}

// 音乐播放器包装组件（TUAC 缺省值从 tuac_core.inf 加载后再挂载播放器）
function MusicPlayerWrapper() {
  const { currentMusic, playlist, playing, playNext, playPrevious, handlePlaylistChange, removeFromPlaylist } = useMusicPlayer();

  return (
    <TuacDefaultsProvider>
      <MusicPlayer
        currentMusic={currentMusic}
        playlist={playlist}
        playing={playing}
        onNext={playNext}
        onPrevious={playPrevious}
        onPlaylistChange={handlePlaylistChange}
        onRemoveFromPlaylist={removeFromPlaylist}
      />
    </TuacDefaultsProvider>
  );
}

export default App; 